import { Button, Dropdown, Flex, type MenuProps } from "antd";
import { useNavigate, useParams } from "react-router";
import {
  useDeleteOptionMutation,
  useDeleteQuestionMutation,
  useGetQuizQuery,
} from "../../../app/features/adminContent/adminContent.api";
import AddIcon from "../../../assets/icons/AddIcon";
import ArrowLeftIcon from "../../../assets/icons/ArrowLeftIcon";
import DeleteIcon from "../../../assets/icons/DeleteIcon";
import EditIcon from "../../../assets/icons/EditIcon";
import ThreeDotIcon from "../../../assets/icons/ThreeDotIcon";
import CustomTable from "../../../components/CustomTable";
import CustomTitle from "../../../components/CustomTitle";
import useError from "../../../hooks/useError";
import useFilters from "../../../hooks/useFilters";
import useModal from "../../../hooks/useModal";
import DashboardLayout from "../../../layouts/DashboardLayout";
import { toast } from "../../../lib/toast";
import type { ColumnsType } from "../../../types/global.types";
import ConfirmModal from "../Dashboard/_components/ConfirmModal";
import ManageOptionsModal from "./_components/ManageOptionsModal";
import ManageQuizModal from "./_components/ManageQuizModal";

export default function ManageQuizDetails() {
  const { courseId, sectionId, quizId } = useParams();
  const navigate = useNavigate();
  const { limit, page, handleLimit, handlePagination } = useFilters();
  const { open, handleOpen, handleClose } = useModal<any, undefined>();
  // Query API
  const { data, isLoading, error } = useGetQuizQuery(Number(quizId), {
    skip: !quizId,
  });

  // Mutation API
  const [deleteQuestion, { isLoading: deleteLoading, error: deleteError }] =
    useDeleteQuestionMutation();
  const [
    deleteOption,
    { isLoading: deleteOptionLoading, error: deleteOptionError },
  ] = useDeleteOptionMutation();

  // Error Handeling
  useError(error, "Quiz Question Get Data Failed");
  useError(deleteError, "Quiz Question Deleted Failed");
  useError(deleteOptionError, "Quiz Question Option Deleted Failed");

  const handleDelete = async () => {
    // Question Delete
    if (open?.data?.id && open?.type === "CONFIRM") {
      try {
        // Call the mutation with the quiz ID
        await deleteQuestion(open.data.id).unwrap(); // <- unwrap ensures we can catch errors
        toast("success", "Quiz Question Deleted Successfully");
        handleClose();
      } catch (error) {
        if (error) {
          toast("error", "Failed to Delete Quiz Question");
        }
      }
    }

    // Option Delete
    if (open?.data?.id && open?.type === "CONFIRM_OPTIONS") {
      try {
        // Call the mutation with the quiz ID
        await deleteOption(open.data.id).unwrap(); // <- unwrap ensures we can catch errors
        toast("success", "Quiz Question Option Deleted Successfully");
        handleClose();
      } catch (error) {
        if (error) {
          toast("error", "Failed to Delete Quiz Question Option");
        }
      }
    }
  };

  const actionMenu = (record: any): MenuProps => ({
    items: [
      {
        key: "create",
        icon: <AddIcon />,
        label: "Create Options",
        onClick: () => handleOpen({ type: "ADD_OPTIONS", data: record }),
      },
      {
        key: "editQuiz",
        icon: <EditIcon />,
        label: "Edit Quiz",
        onClick: () => {
          handleOpen({
            type: "UPDATE",
            data: record, // ✅ this EXISTS in ModalType
          });
        },
      },
      {
        key: "deleteQuiz",
        icon: <DeleteIcon />,
        label: "Delete Quiz",
        onClick: () => {
          handleOpen({ type: "CONFIRM", data: record });
        },
      },
    ],
  });

  const columns: ColumnsType<any> = [
    {
      title: "Quiz Title",
      dataIndex: "prompt",
      width: 300,
      render: (val) => <span>{val}</span>,
    },
    {
      title: "Quiz Options",
      dataIndex: "QuizOption",
      width: 400,
      render: (options: any[]) => (
        <ul className="m-0 p-0">
          {" "}
          {/* ensure padding so items show */}
          {options.map((opt) => (
            <li key={opt.id} className="flex items-center gap-3 mb-2">
              {/* Custom Circle */}
              <span
                className={`flex-shrink-0 w-2 h-2 rounded-full ${
                  opt.isCorrect ? "bg-green-500" : "bg-red-500"
                }`}
              ></span>

              {/* Option Text */}
              <span className="flex-1 text-gray-800">{opt.text}</span>

              {/* Edit and Delete Icon */}
              <span className="flex justify-start items-center gap-2">
                <span
                  className="cursor-pointer text-blue-500 hover:text-blue-700 ml-2"
                  onClick={() =>
                    handleOpen({
                      type: "UPDATE_OPTIONS",
                      data: opt,
                    })
                  }
                >
                  <EditIcon />
                </span>

                <span
                  className="cursor-pointer text-blue-500 hover:text-blue-700 ml-2"
                  onClick={() =>
                    handleOpen({
                      type: "CONFIRM_OPTIONS",
                      data: opt,
                    })
                  }
                >
                  <DeleteIcon className="text-red-500" />
                </span>
              </span>
            </li>
          ))}
        </ul>
      ),
    },
    {
      title: "Quiz Mark",
      dataIndex: "marks",
      align: "center",
      width: 300,
      render: (val) => <span>{val}</span>,
    },
    {
      title: "Quiz Type",
      dataIndex: "type",
      align: "center",
      width: 300,
      render: (val: string) => (
        <span>
          {val === "SINGLE_SELECT"
            ? "Single Select"
            : val === "MULTI_SELECT"
              ? "Multi Select"
              : val}
        </span>
      ),
    },

    {
      title: "Sort Order",
      dataIndex: "sortOrder",
      align: "center",
      width: 300,
      render: (val) => <span>{val}</span>,
    },

    {
      title: "Action",
      dataIndex: "id",
      key: "action",
      render: (_: any, record: any) => (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
          }}
        >
          <Dropdown menu={actionMenu(record)} trigger={["click"]}>
            <span
              style={{ cursor: "pointer", lineHeight: 0 }}
              onClick={(e) => e.preventDefault()}
            >
              <ThreeDotIcon className="text-lg" />
            </span>
          </Dropdown>
        </div>
      ),
    },
  ];

  return (
    <DashboardLayout title="Quiz Question Details">
      <Flex justify="space-between" align="center" className="mb-4">
        <Flex justify="start" gap={16}>
          <Button
            type="default"
            size="middle"
            className="btn-bordered-primary border
               border-zinc-200 hover:!border-zinc-200 focus:!border-zinc-200 active:!border-zinc-200"
            onClick={() =>
              navigate(
                `/admin/my-course/manage-course/manage-quiz/${courseId}/${sectionId}`,
              )
            }
          >
            <ArrowLeftIcon /> Back
          </Button>
          <CustomTitle
            title="Quiz Qutions Details"
            description="Manage quizzes, track learners performance"
          />
        </Flex>

        <Button
          type="primary"
          className="shadow-sm"
          onClick={() => handleOpen({ type: "ADD" })}
        >
          Create Quiz
        </Button>
      </Flex>

      <CustomTable
        tableTitle="My Quiz Questions"
        columns={columns}
        dataSource={data?.QuizQuestion ?? []}
        limit={limit}
        page={page}
        total={data?.QuizQuestion?.length ?? 0}
        onLimitChange={handleLimit}
        onPaginate={handlePagination}
        loading={isLoading}
        hideRowSelect
        filterSection={<></>}
      />

      {(open?.type === "ADD" || open?.type === "UPDATE") && (
        <ManageQuizModal
          open={open?.open}
          onCancel={handleClose}
          initialValues={open?.data}
          type={open?.type}
        />
      )}

      {(open?.type === "ADD_OPTIONS" || open?.type === "UPDATE_OPTIONS") && (
        <ManageOptionsModal
          open={open?.open}
          onCancel={handleClose}
          initialValues={open?.data}
          type={open?.type}
        />
      )}

      {(open?.type === "CONFIRM" || open?.type === "CONFIRM_OPTIONS") && (
        <ConfirmModal
          loading={deleteLoading || deleteOptionLoading}
          open={open?.open}
          onCancel={handleClose}
          title="Are you sure you want to delete this item?"
          subTitle="This action cannot be undone. Once deleted, the item will be permanently removed."
          handleConfirm={handleDelete}
        />
      )}
    </DashboardLayout>
  );
}
